import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail, Phone, MessageSquare, MailCheck } from "lucide-react";
import { CONTACT_PHONE, CONTACT_PHONE_HREF } from "@/components/Navbar";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("email"); // "email" | "sms"

  // Email flow
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // SMS flow
  const [phone, setPhone] = useState("");
  const [smsSent, setSmsSent] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Enter your email");
    setEmailLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setEmailSent(true);
    } catch (err) { toast.error(extractError(err)); }
    finally { setEmailLoading(false); }
  };

  const submitSms = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return toast.error("Enter your mobile number");
    setSmsLoading(true);
    try {
      await api.post("/auth/forgot-password-sms", { phone: phone.trim() });
      setSmsSent(true);
      toast.success("If your number is on file, a 6-digit code is on the way.");
    } catch (err) { toast.error(extractError(err)); }
    finally { setSmsLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter the 6-digit code");
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters");
    setResetLoading(true);
    try {
      await api.post("/auth/reset-password-sms", {
        phone: phone.trim(),
        code: code.trim(),
        new_password: newPassword,
      });
      toast.success("Password reset — welcome back!");
      navigate("/family"); // auto-login; router will redirect based on role
    } catch (err) { toast.error(extractError(err)); }
    finally { setResetLoading(false); }
  };

  const Tab = ({ id, icon, label }) => (
    <button
      type="button"
      onClick={() => setMode(id)}
      data-testid={`forgot-tab-${id}`}
      className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
        mode === id
          ? "bg-sage-600 text-white shadow-sm"
          : "bg-white text-sage-800 hover:bg-sage-50 border border-stone2-200"
      }`}
    >
      {icon} {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="forgot-password-page">
      <Navbar />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-14">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold mb-4 hover:text-sage-900">
          <ArrowLeft className="w-4 h-4" /> Back to log in
        </Link>
        <div className="paper-card p-6 sm:p-8">
          <h1 className="font-heading font-bold text-sage-900 text-2xl">Reset your password</h1>

          {/* Support-first banner — visible before anyone tries the (dormant) automated flows */}
          <div className="mt-4 rounded-xl border-2 border-ochre-300 bg-ochre-50 p-4 text-sm text-stone2-800 leading-relaxed"
               data-testid="forgot-support-banner">
            <div className="font-heading font-bold text-sage-900 flex items-center gap-1.5 text-base">
              <MailCheck className="w-4 h-4 text-ochre-700" /> Fastest way to reset:
            </div>
            <p className="mt-1">
              Send an email to{" "}
              <a href="mailto:contactsupport@autismbabysitters.com?subject=Password%20reset%20request"
                 className="font-bold text-ochre-800 underline"
                 data-testid="forgot-support-email">
                contactsupport@autismbabysitters.com
              </a>{" "}
              and include:
            </p>
            <ul className="mt-1 pl-5 list-disc space-y-0.5">
              <li>Your <strong>full name</strong></li>
              <li>The <strong>email address</strong> you used to sign up</li>
            </ul>
            <p className="mt-2 text-xs text-stone2-600">
              We'll reply with a temporary password within one business day.
            </p>
          </div>

          <p className="text-sm text-stone2-600 mt-4">
            You can also try our automated reset options below (currently limited availability):
          </p>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <Tab id="email" icon={<Mail className="w-4 h-4" />} label="By email" />
            <Tab id="sms" icon={<MessageSquare className="w-4 h-4" />} label="By text" />
          </div>

          {/* ============ EMAIL FLOW ============ */}
          {mode === "email" && !emailSent && (
            <form onSubmit={submitEmail} className="mt-6 space-y-4" data-testid="forgot-email-form">
              <div>
                <label className="field-label" htmlFor="email">Email on your account</label>
                <input id="email" type="email" required autoFocus
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>
              <button type="submit" disabled={emailLoading} data-testid="forgot-submit" className="btn-primary w-full justify-center">
                {emailLoading && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
              </button>
            </form>
          )}

          {mode === "email" && emailSent && (
            <div className="mt-6 space-y-4" data-testid="forgot-sent-state">
              <div className="rounded-xl bg-sage-50 border border-sage-200 p-4">
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-sage-900">
                  <Mail className="w-4 h-4" /> Check your email
                </div>
                <p className="mt-2 text-sm text-stone2-700 leading-relaxed">
                  If an account exists for <strong>{email}</strong>, a reset link is on its way.
                  The link expires in 60 minutes.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setEmailSent(false); setMode("sms"); }}
                className="btn-outline-sage w-full justify-center"
                data-testid="forgot-try-sms-instead"
              >
                <MessageSquare className="w-4 h-4" /> Try text message instead
              </button>
              <div className="rounded-xl border border-stone2-200 p-4 text-sm text-stone2-600">
                <p className="font-semibold text-sage-900">Didn't get an email?</p>
                <ul className="mt-1 space-y-1 list-disc pl-5">
                  <li>Check your spam folder</li>
                  <li>Make sure you typed the correct email</li>
                  <li>
                    <strong className="text-sage-900">Fastest:</strong> email{" "}
                    <a href="mailto:contactsupport@autismbabysitters.com?subject=Password%20reset%20request"
                       className="text-ochre-700 font-semibold underline">
                      contactsupport@autismbabysitters.com
                    </a>{" "}
                    with your full name and signup email
                  </li>
                  <li>
                    Or call us at{" "}
                    <a href={CONTACT_PHONE_HREF} className="text-ochre-700 font-semibold">
                      <Phone className="inline w-3.5 h-3.5" /> {CONTACT_PHONE}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* ============ SMS FLOW ============ */}
          {mode === "sms" && !smsSent && (
            <form onSubmit={submitSms} className="mt-6 space-y-4" data-testid="forgot-sms-form">
              <div>
                <label className="field-label" htmlFor="phone">Mobile phone on your account</label>
                <input id="phone" type="tel" required autoFocus
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="(770) 555-1234"
                  data-testid="forgot-phone-input"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
                <p className="mt-1.5 text-xs text-stone2-500">
                  You'll get a 6-digit code via SMS. Standard message &amp; data rates may apply.
                </p>
              </div>
              <button type="submit" disabled={smsLoading} data-testid="forgot-sms-submit" className="btn-primary w-full justify-center">
                {smsLoading && <Loader2 className="w-4 h-4 animate-spin" />} Send code by text
              </button>
            </form>
          )}

          {mode === "sms" && smsSent && (
            <form onSubmit={submitReset} className="mt-6 space-y-4" data-testid="forgot-sms-code-form">
              <div className="rounded-xl bg-sage-50 border border-sage-200 p-4 text-sm text-stone2-700">
                If <strong>{phone}</strong> is on file, we sent a 6-digit code. It expires in 15 min.
              </div>
              <div>
                <label className="field-label" htmlFor="code">6-digit code</label>
                <input id="code" type="text" inputMode="numeric" required autoFocus
                  maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  data-testid="forgot-code-input"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-lg font-mono tracking-widest text-center focus:ring-2 focus:ring-sage-500" />
              </div>
              <div>
                <label className="field-label" htmlFor="newpw">New password (min 8)</label>
                <input id="newpw" type="password" required minLength={8}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="forgot-newpassword-input"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
                <p className="mt-1.5 text-xs text-stone2-500">
                  Must contain at least one letter and one number.
                </p>
              </div>
              <button type="submit" disabled={resetLoading} data-testid="forgot-reset-submit" className="btn-primary w-full justify-center">
                {resetLoading && <Loader2 className="w-4 h-4 animate-spin" />} Reset password
              </button>
              <button
                type="button"
                onClick={() => { setSmsSent(false); setCode(""); setNewPassword(""); }}
                className="w-full text-xs text-sage-700 hover:text-sage-900 font-semibold"
                data-testid="forgot-resend-code"
              >
                Didn't get it? Resend a new code
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
